import * as React from 'react';
import { useAuth0 } from '@auth0/auth0-react';

// Simple in-memory cache keyed by apiBase string
const agentsCache = new Map(); // apiBase -> { data, ts }

/**
 * useAgents
 * Unified agent fetching logic with optional enabling + manual refresh.
 * De-duplicates concurrent loads and memoizes per apiBase during session.
 */
export function useAgents({ apiBase, enabled, cacheTTL = 60_000 } = {}) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const [agents, setAgents] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const inflightRef = React.useRef(null);

  const load = React.useCallback(async (force = false) => {
    if (!enabled) return;
    const cacheKey = apiBase || '';
    const now = Date.now();
    const cached = agentsCache.get(cacheKey);
    if (!force && cached && (now - cached.ts) < cacheTTL) {
      setAgents(cached.data || []);
      return;
    }
    if (inflightRef.current) return; // prevent duplicate fetch while one in flight
    inflightRef.current = true;
    setLoading(true); setError(null);
    let alive = true;
    try {
      const headers = {};
      if (isAuthenticated) {
        try { headers.Authorization = 'Bearer ' + await getAccessTokenSilently(); } catch (e) { /* ignore */ }
      }
      const url = (apiBase || '') + '/api/agents';
      console.log('[useAgents] GET', url, force ? '(force)' : '');
      const r = await fetch(url, { headers });
      if (!r.ok) throw new Error('Agents load failed: ' + r.status);
      const data = await r.json();
      if (alive) {
        setAgents(Array.isArray(data) ? data : []);
        agentsCache.set(cacheKey, { data: Array.isArray(data) ? data : [], ts: now });
      }
    } catch (err) {
      if (alive) setError(err.message);
    } finally {
      if (alive) setLoading(false);
      inflightRef.current = null;
    }
    return () => { alive = false; };
  }, [apiBase, cacheTTL, enabled, getAccessTokenSilently, isAuthenticated]);

  // Initial load
  React.useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const refresh = React.useCallback(() => load(true), [load]);

  return { agents, loading, error, refresh };
}
